import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  FaArrowDown, FaArrowUp, FaCheckSquare,
  FaClone, FaDownload, FaFileImage, FaFilePdf, FaFont, FaGripVertical,
  FaLayerGroup, FaPlus, FaRedo, FaRegSquare, FaSyncAlt, FaTimes, FaTrash,
} from "react-icons/fa";
import {
  extractPdfTextPages, renderPdfPagePreview, renderPdfPagesAsPng, renderPdfThumbnails,
} from "./pdfPreview";

import PdfPageEditor from "./PdfPageEditor";
import { applyPdfCrop, drawPdfAnnotations, pageDisplaySize } from "./pdfAnnotations";

const MAX_FILE_SIZE = 100 * 1024 * 1024;
const loadPdfLib = () => import("pdf-lib");

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(value >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
};

const isPdf = (file) => file?.type === "application/pdf"
  || String(file?.name || "").toLowerCase().endsWith(".pdf");
const isImage = (file) => /\.(png|jpe?g)$/i.test(file?.name || "")
  || ["image/png", "image/jpeg"].includes(file?.type);

export const imageToPdfBytes = async (file) => {
  const { PDFDocument } = await loadPdfLib();
  const document = await PDFDocument.create();
  const bytes = await file.arrayBuffer();
  const signature = new Uint8Array(bytes);
  const image = signature[0] === 0x89 && signature[1] === 0x50
    ? await document.embedPng(bytes)
    : await document.embedJpg(bytes);
  const page = document.addPage([image.width, image.height]);
  page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  return document.save();
};

const safeBaseName = (name) => String(name || "PDF").replace(/\.pdf$/i, "").replace(/[\\/:*?"<>|]/g, "-");

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const movePdfItem = (items, sourceId, targetId) => {
  if (!sourceId || !targetId || sourceId === targetId) return items;
  const sourceIndex = items.findIndex((item) => item.id === sourceId);
  const targetIndex = items.findIndex((item) => item.id === targetId);
  if (sourceIndex < 0 || targetIndex < 0) return items;
  const next = [...items];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
};

export const rotatePdfPages = (pages, selectedIds, amount) => pages.map((page) => (
  selectedIds.has(page.id)
    ? { ...page, rotation: ((page.rotation + amount) % 360 + 360) % 360 }
    : page
));

export const duplicatePdfPages = (pages, selectedIds, makeId) => {
  const next = [];
  pages.forEach((page) => {
    next.push(page);
    if (selectedIds.has(page.id)) next.push({ ...page, id: makeId() });
  });
  return next;
};

const getOutputName = (value) => {
  const trimmed = String(value || "").trim() || "合併文件";
  return trimmed.toLowerCase().endsWith(".pdf") ? trimmed : `${trimmed}.pdf`;
};

const groupPageSpecsByFile = (pageSpecs) => {
  const groups = new Map();
  pageSpecs.forEach((page) => {
    if (!groups.has(page.fileId)) groups.set(page.fileId, []);
    groups.get(page.fileId).push(page);
  });
  return groups;
};

export default function PdfMergeWorkspace() {
  const inputRef = useRef(null);
  const imageInputRef = useRef(null);
  const idRef = useRef(0);
  const dragIdRef = useRef(null);
  const pageDragIdRef = useRef(null);
  const activeFileIdsRef = useRef(new Set());
  const previewRequestRef = useRef(0);
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedPageIds, setSelectedPageIds] = useState(new Set());
  const [activeView, setActiveView] = useState("pages");
  const [dropActive, setDropActive] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [pageDragOverId, setPageDragOverId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(false);
  const [outputName, setOutputName] = useState("合併文件.pdf");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [extractedText, setExtractedText] = useState("");
  const [previewPage, setPreviewPage] = useState(null);

  const totalSize = useMemo(
    () => items.reduce((sum, item) => sum + (item.file.size || 0), 0),
    [items],
  );
  const selectedPages = useMemo(
    () => pages.filter((page) => selectedPageIds.has(page.id)),
    [pages, selectedPageIds],
  );
  const resetResult = () => { setStatus("idle"); setMessage(""); };
  const makePageId = () => `page-${Date.now()}-${idRef.current++}`;

  const closePagePreview = () => {
    previewRequestRef.current += 1;
    setPreviewPage(null);
  };

  const openPagePreview = async (page) => {
    const source = items.find((item) => item.id === page.fileId);
    if (!source || !page.thumbnail) return;
    const requestId = ++previewRequestRef.current;
    setPreviewPage({
      ...page, image: page.thumbnail, imageRotation: 0, fit: true, loading: true,
    });
    try {
      const image = await renderPdfPagePreview(source.file, page.pageIndex, page.rotation);
      if (previewRequestRef.current === requestId) {
        setPreviewPage((current) => current ? {
          ...current, image, imageRotation: page.rotation, loading: false,
        } : null);
      }
    } catch (error) {
      console.error("PDF enlarged preview rendering failed", error);
      if (previewRequestRef.current === requestId) {
        setPreviewPage((current) => current ? { ...current, loading: false } : null);
      }
    }
  };

  const rotatePreviewPage = async (amount) => {
    if (!previewPage) return;
    const nextRotation = ((previewPage.rotation + amount) % 360 + 360) % 360;
    const requestId = ++previewRequestRef.current;
    setPages((current) => current.map((page) => (
      page.id === previewPage.id ? { ...page, rotation: nextRotation } : page
    )));
    setPreviewPage((current) => current ? { ...current, rotation: nextRotation, loading: true } : null);
    resetResult();

    const source = items.find((item) => item.id === previewPage.fileId);
    if (!source) return;
    try {
      const image = await renderPdfPagePreview(source.file, previewPage.pageIndex, nextRotation);
      if (previewRequestRef.current === requestId) {
        setPreviewPage((current) => current ? {
          ...current, image, imageRotation: nextRotation, loading: false,
        } : null);
      }
    } catch (error) {
      console.error("PDF enlarged preview rotation failed", error);
      if (previewRequestRef.current === requestId) {
        setPreviewPage((current) => current ? { ...current, loading: false } : null);
      }
    }
  };

  const showAdjacentPreview = (offset) => {
    if (!previewPage) return;
    const index = pages.findIndex((page) => page.id === previewPage.id);
    const target = pages[index + offset];
    if (target) openPagePreview(target);
  };

  useEffect(() => {
    if (!previewPage) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePagePreview();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewPage]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const validFiles = files.filter((file) => isPdf(file) || isImage(file));
    const oversized = validFiles.filter((file) => file.size > MAX_FILE_SIZE);
    const accepted = validFiles.filter((file) => file.size <= MAX_FILE_SIZE);
    const pendingItems = accepted.map((file) => ({
      id: `pdf-${Date.now()}-${idRef.current++}`,
      file, pageCount: 0, state: "loading", error: "",
    }));

    if (pendingItems.length) {
      pendingItems.forEach((item) => activeFileIdsRef.current.add(item.id));
      setItems((current) => [...current, ...pendingItems]);
      setStatus("loading");
      setMessage("正在讀取檔案並建立 PDF 頁面…");
      const { PDFDocument } = await loadPdfLib();
      const inspected = await Promise.all(pendingItems.map(async (item) => {
        try {
          let sourceFile = item.file;
          if (isImage(sourceFile)) {
            const bytes = await imageToPdfBytes(sourceFile);
            sourceFile = new File([bytes], `${sourceFile.name}.pdf`, { type: "application/pdf" });
          }
          const document = await PDFDocument.load(await sourceFile.arrayBuffer(), { updateMetadata: false });
          const pageCount = document.getPageCount();
          return {
            ...item, file: sourceFile, pageCount, state: "ready",
            pages: Array.from({ length: pageCount }, (_, pageIndex) => ({
              id: makePageId(), fileId: item.id, fileName: item.file.name,
              pageIndex, sourcePageNumber: pageIndex + 1, rotation: 0,
              thumbnail: "", thumbnailState: "loading",
              ...pageDisplaySize(document.getPage(pageIndex)), annotations: [],
            })),
          };
        } catch {
          return { ...item, state: "error", error: "檔案無法讀取，可能已加密或檔案損壞。", pages: [] };
        }
      }));
      setItems((current) => current.map((currentItem) => {
        const result = inspected.find((item) => item.id === currentItem.id);
        return result ? { ...currentItem, file: result.file, pageCount: result.pageCount, state: result.state, error: result.error } : currentItem;
      }));
      const activeInspected = inspected.filter((item) => activeFileIdsRef.current.has(item.id));
      setPages((current) => [...current, ...activeInspected.flatMap((item) => item.pages)]);
      const hasError = activeInspected.some((item) => item.state === "error");
      setStatus(hasError ? "error" : "idle");
      setMessage(hasError ? "部分檔案無法讀取；其他檔案仍可繼續編輯。" : "");

      activeInspected.filter((item) => item.state === "ready").forEach(async (item) => {
        try {
          const thumbnails = (await renderPdfThumbnails(item.file, item.pageCount)) || [];
          setPages((current) => current.map((page) => page.fileId === item.id
            ? { ...page, thumbnail: thumbnails[page.pageIndex] || "", thumbnailState: "ready" }
            : page));
        } catch (error) {
          console.error("PDF thumbnail rendering failed", error);
          setPages((current) => current.map((page) => page.fileId === item.id
            ? { ...page, thumbnailState: "error", thumbnailError: error?.message || "縮圖載入失敗" }
            : page));
        }
      });
    }
    if (validFiles.length !== files.length) {
      setStatus("idle");
      setMessage("已略過不支援的檔案；請選擇 PDF、JPG 或 PNG。");
    } else if (oversized.length) {
      setStatus("idle");
      setMessage("單一檔案上限為 100 MB，過大的檔案已略過。");
    }
  };

  const removeItem = (id) => {
    activeFileIdsRef.current.delete(id);
    const removedPageIds = new Set(pages.filter((page) => page.fileId === id).map((page) => page.id));
    setItems((current) => current.filter((item) => item.id !== id));
    setPages((current) => current.filter((page) => page.fileId !== id));
    setSelectedPageIds((current) => new Set([...current].filter((pageId) => !removedPageIds.has(pageId))));
    resetResult();
  };

  const reorderFiles = (sourceId, targetId) => {
    const nextItems = movePdfItem(items, sourceId, targetId);
    setItems(nextItems);
    setPages((current) => {
      const grouped = groupPageSpecsByFile(current);
      return nextItems.flatMap((item) => grouped.get(item.id) || []);
    });
    resetResult();
  };

  const moveItemBy = (id, offset) => {
    const sourceIndex = items.findIndex((item) => item.id === id);
    const target = items[sourceIndex + offset];
    if (sourceIndex >= 0 && target) reorderFiles(id, target.id);
  };

  const togglePage = (id) => setSelectedPageIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const clearAll = () => {
    closePagePreview();
    setEditingOrder(false); pageDragIdRef.current = null; setPageDragOverId(null);
    activeFileIdsRef.current.clear();
    setItems([]); setPages([]); setSelectedPageIds(new Set()); setExtractedText(""); resetResult();
  };

  const applySelectedPageChange = (action) => {
    if (!selectedPageIds.size) return;
    if (action === "rotate-left") setPages((current) => rotatePdfPages(current, selectedPageIds, -90));
    if (action === "rotate-right") setPages((current) => rotatePdfPages(current, selectedPageIds, 90));
    if (action === "duplicate") {
      setPages((current) => duplicatePdfPages(current, selectedPageIds, makePageId));
      setSelectedPageIds(new Set());
    }
    if (action === "delete") {
      setPages((current) => current.filter((page) => !selectedPageIds.has(page.id)));
      setSelectedPageIds(new Set());
    }
    resetResult();
  };

  const buildPdf = async (outputPages) => {
    const { PDFDocument, degrees } = await loadPdfLib();
    const output = await PDFDocument.create();
    const sources = new Map();
    for (const pageSpec of outputPages) {
      if (!sources.has(pageSpec.fileId)) {
        const item = items.find((candidate) => candidate.id === pageSpec.fileId);
        if (!item) continue;
        sources.set(pageSpec.fileId, await PDFDocument.load(await item.file.arrayBuffer(), { updateMetadata: false }));
      }
      const [copiedPage] = await output.copyPages(sources.get(pageSpec.fileId), [pageSpec.pageIndex]);
      await drawPdfAnnotations(output, copiedPage, pageSpec.annotations);
      applyPdfCrop(copiedPage, pageSpec.crop);
      const sourceRotation = copiedPage.getRotation().angle || 0;
      copiedPage.setRotation(degrees((sourceRotation + pageSpec.rotation + 360) % 360));
      output.addPage(copiedPage);
    }
    return output.save();
  };

  const exportPdf = async (outputPages, filename, successMessage) => {
    if (!outputPages.length) return;
    setStatus("loading"); setMessage("正在依照目前頁面順序產生 PDF…");
    try {
      const bytes = await buildPdf(outputPages);
      downloadBlob(new Blob([bytes], { type: "application/pdf" }), filename);
      setStatus("success"); setMessage(successMessage);
    } catch {
      setStatus("error"); setMessage("PDF 產生失敗，請移除無法讀取或設有密碼的檔案後再試一次。");
    }
  };

  const extractSelectedText = async () => {
    if (!selectedPages.length) return;
    setStatus("loading"); setMessage("正在擷取選取頁面的文字…");
    try {
      const grouped = groupPageSpecsByFile(selectedPages);
      const textByFile = new Map();
      for (const [fileId, filePages] of grouped) {
        const item = items.find((candidate) => candidate.id === fileId);
        if (item) textByFile.set(fileId, await extractPdfTextPages(item.file, filePages.map((page) => page.pageIndex)));
      }
      const text = selectedPages.map((page, index) => {
        const pageText = textByFile.get(page.fileId)?.[page.pageIndex] || "";
        return `--- ${index + 1}. ${page.fileName}・第 ${page.sourcePageNumber} 頁 ---\n${pageText || "（此頁沒有可選取文字，掃描頁請改用文件解析 OCR。）"}`;
      }).join("\n\n");
      setExtractedText(text); setActiveView("text"); setStatus("success");
      setMessage(`已擷取 ${selectedPages.length} 頁文字。`);
    } catch {
      setStatus("error"); setMessage("文字擷取失敗；掃描 PDF 請改用「文件解析」進行 OCR。");
    }
  };

  const exportSelectedImages = async () => {
    if (!selectedPages.length) return;
    setStatus("loading"); setMessage("正在把選取頁面轉成 PNG…");
    try {
      const bytes = await buildPdf(selectedPages);
      const file = { arrayBuffer: async () => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) };
      const images = await renderPdfPagesAsPng(file, selectedPages.map((page, index) => ({ id: page.id, pageIndex: index, rotation: 0 })));
      const rendered = new Map(images.map((image) => [image.id, image.blob]));
      selectedPages.forEach((page, index) => {
        const blob = rendered.get(page.id);
        if (blob) downloadBlob(blob, `${safeBaseName(page.fileName)}-第${page.sourcePageNumber}頁-${index + 1}.png`);
      });
      setStatus("success"); setMessage(`已匯出 ${selectedPages.length} 張 PNG 圖片。`);
    } catch {
      setStatus("error"); setMessage("頁面圖片匯出失敗，請縮小選取範圍後重試。");
    }
  };

  const copyExtractedText = async () => {
    try {
      await navigator.clipboard.writeText(extractedText);
      setMessage("文字已複製到剪貼簿。"); setStatus("success");
    } catch {
      setStatus("error"); setMessage("無法自動複製，請在文字框內全選複製。");
    }
  };

  const hasLoadingFile = items.some((item) => item.state === "loading");
  const canExport = pages.length > 0 && !hasLoadingFile && status !== "loading";
  const allPagesSelected = pages.length > 0 && selectedPageIds.size === pages.length;
  const previewPageIndex = previewPage
    ? pages.findIndex((page) => page.id === previewPage.id)
    : -1;

  return (
    <>
    <EditorShell>
      <EditorSidebar>
        <SidebarHeading><span>PDF 編輯</span><small>檔案只在你的瀏覽器內處理，不會上傳</small></SidebarHeading>
        <FileDropZone
          $active={dropActive}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => { event.preventDefault(); setDropActive(true); }}
          onDragLeave={() => setDropActive(false)}
          onDrop={(event) => { event.preventDefault(); setDropActive(false); addFiles(event.dataTransfer.files); }}
        >
          <DropIcon><FaPlus /></DropIcon><strong>加入 PDF 或圖片</strong>
          <span>點擊選擇，或直接拖曳檔案到這裡</span>
          <input ref={inputRef} type="file" accept="application/pdf,.pdf,image/png,image/jpeg,.png,.jpg,.jpeg" multiple onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} />
        </FileDropZone>
        <ClearButton type="button" disabled={status === "loading"} onClick={() => imageInputRef.current?.click()}><FaFileImage /> 多個圖片合成 PDF</ClearButton>
        <input ref={imageInputRef} aria-label="選擇多個圖片合成 PDF" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" multiple hidden onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} />
        <small>支援 JPG、PNG，每張圖片一頁，保留原比例；加入後可拖曳排序再匯出。</small>
        <Field><label htmlFor="pdf-output-name">輸出檔名</label><input id="pdf-output-name" value={outputName} onChange={(event) => setOutputName(event.target.value)} placeholder="合併文件.pdf" /></Field>
        <SummaryGrid>
          <SummaryItem><strong>{items.length}</strong><span>來源檔案</span></SummaryItem>
          <SummaryItem><strong>{pages.length || "—"}</strong><span>輸出頁數</span></SummaryItem>
          <SummaryItem><strong>{formatBytes(totalSize)}</strong><span>原始大小</span></SummaryItem>
        </SummaryGrid>
        <MergeButton type="button" $loading={status === "loading"} disabled={!canExport} onClick={() => exportPdf(pages, getOutputName(outputName), `PDF 已完成，共 ${pages.length} 頁，已開始下載。`)}>
          {status === "loading" ? <FaSyncAlt /> : <FaDownload />}{status === "loading" ? "處理中" : "匯出完整 PDF"}
        </MergeButton>
        {message && <StatusMessage $status={status}>{message}</StatusMessage>}
      </EditorSidebar>

      <EditorMain>
        <ViewTabs aria-label="PDF 編輯模式">
          <ViewTab type="button" $active={activeView === "pages"} onClick={() => setActiveView("pages")}><FaLayerGroup /> 頁面整理</ViewTab>
          <ViewTab type="button" $active={activeView === "files"} onClick={() => setActiveView("files")}><FaFilePdf /> 來源檔案</ViewTab>
          <ViewTab type="button" $active={activeView === "text"} onClick={() => setActiveView("text")}><FaFont /> 文字擷取</ViewTab>
        </ViewTabs>

        {activeView === "files" ? (
          <Panel>
            <PanelHeader><div><h3>來源檔案順序</h3><p>拖曳整份檔案會重新排列該檔案的所有頁面。</p></div>{items.length > 0 && <ClearButton type="button" onClick={clearAll}><FaTrash /> 清除全部</ClearButton>}</PanelHeader>
            {items.length ? <FileList aria-label="PDF 合併順序">{items.map((item, index) => (
              <FileRow
                key={item.id} draggable={status !== "loading"} $dragOver={dragOverId === item.id}
                onDragStart={(event) => { dragIdRef.current = item.id; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", item.id); }}
                onDragOver={(event) => { event.preventDefault(); setDragOverId(item.id); }}
                onDragLeave={() => setDragOverId((current) => current === item.id ? null : current)}
                onDrop={(event) => { event.preventDefault(); reorderFiles(dragIdRef.current || event.dataTransfer.getData("text/plain"), item.id); setDragOverId(null); }}
                onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
              >
                <DragHandle title="拖曳排序"><FaGripVertical /></DragHandle><OrderNumber>{index + 1}</OrderNumber><PdfIcon><FaFilePdf /></PdfIcon>
                <FileDetails><strong title={item.file.name}>{item.file.name}</strong><span>{item.state === "loading" ? "讀取頁數中…" : item.state === "error" ? item.error : `${item.pageCount} 頁 ・ ${formatBytes(item.file.size)}`}</span></FileDetails>
                <RowActions>
                  <SmallButton type="button" aria-label={`上移 ${item.file.name}`} title="上移" disabled={index === 0 || status === "loading"} onClick={() => moveItemBy(item.id, -1)}><FaArrowUp /></SmallButton>
                  <SmallButton type="button" aria-label={`下移 ${item.file.name}`} title="下移" disabled={index === items.length - 1 || status === "loading"} onClick={() => moveItemBy(item.id, 1)}><FaArrowDown /></SmallButton>
                  <SmallButton type="button" aria-label={`移除 ${item.file.name}`} title="移除" disabled={status === "loading"} onClick={() => removeItem(item.id)}><FaTimes /></SmallButton>
                </RowActions>
              </FileRow>
            ))}</FileList> : <EmptyState onChoose={() => inputRef.current?.click()} />}
          </Panel>
        ) : activeView === "text" ? (
          <Panel>
            <PanelHeader><div><h3>文字擷取</h3><p>可擷取頁面的原生文字；掃描 PDF 請使用「文件解析」OCR。</p></div>{extractedText && <HeaderActions><ClearButton type="button" onClick={copyExtractedText}>複製文字</ClearButton><ClearButton type="button" onClick={() => downloadBlob(new Blob([extractedText], { type: "text/plain;charset=utf-8" }), `${safeBaseName(outputName)}.txt`)}><FaDownload /> TXT</ClearButton></HeaderActions>}</PanelHeader>
            {extractedText ? <TextOutput aria-label="PDF 擷取文字" value={extractedText} onChange={(event) => setExtractedText(event.target.value)} /> : <TextEmpty><FaFont /><strong>尚未擷取文字</strong><span>到「頁面整理」勾選頁面，再按「擷取文字」。</span><button type="button" onClick={() => setActiveView("pages")}>前往選取頁面</button></TextEmpty>}
          </Panel>
        ) : (
          <Panel>
            <PanelHeader><div><h3>頁面整理</h3><p>按「編輯順序」後可拖曳頁面排序；勾選後可批次旋轉、複製、刪除或擷取。</p></div>{pages.length > 0 && <ClearButton type="button" onClick={clearAll}><FaTrash /> 清除全部</ClearButton>}</PanelHeader>
            {pages.length > 0 ? <>
              <PageToolbar>
                <ToolbarButton type="button" $active={allPagesSelected} onClick={() => setSelectedPageIds(allPagesSelected ? new Set() : new Set(pages.map((page) => page.id)))}>{allPagesSelected ? <FaCheckSquare /> : <FaRegSquare />} {allPagesSelected ? "取消全選" : "全選"}</ToolbarButton>
                <ToolbarButton type="button" $active={editingOrder} aria-pressed={editingOrder} disabled={status === "loading"} onClick={() => { setEditingOrder(!editingOrder); pageDragIdRef.current = null; setPageDragOverId(null); }}>{editingOrder ? "完成排序" : "編輯順序"}</ToolbarButton>
                <SelectionCount>{selectedPageIds.size ? `已選 ${selectedPageIds.size} 頁` : "點縮圖左上角勾選頁面"}</SelectionCount><ToolbarDivider />
                <ToolbarButton type="button" disabled={!selectedPageIds.size} onClick={() => applySelectedPageChange("rotate-left")}><FaRedo style={{ transform: "scaleX(-1)" }} /> 左轉</ToolbarButton>
                <ToolbarButton type="button" disabled={!selectedPageIds.size} onClick={() => applySelectedPageChange("rotate-right")}><FaRedo /> 右轉</ToolbarButton>
                <ToolbarButton type="button" disabled={!selectedPageIds.size} onClick={() => applySelectedPageChange("duplicate")}><FaClone /> 複製</ToolbarButton>
                <ToolbarButton type="button" disabled={!selectedPageIds.size} onClick={() => applySelectedPageChange("delete")}><FaTrash /> 刪除</ToolbarButton><ToolbarDivider />
                <ToolbarButton type="button" disabled={!selectedPages.length || status === "loading"} onClick={() => exportPdf(selectedPages, `${safeBaseName(outputName)}-選取頁面.pdf`, `已將 ${selectedPages.length} 頁另存為 PDF。`)}><FaDownload /> 另存選取頁</ToolbarButton>
                <ToolbarButton type="button" disabled={!selectedPages.length || status === "loading"} onClick={extractSelectedText}><FaFont /> 擷取文字</ToolbarButton>
                <ToolbarButton type="button" disabled={!selectedPages.length || status === "loading"} onClick={exportSelectedImages}><FaFileImage /> 匯出 PNG</ToolbarButton>
              </PageToolbar>
              <PageGrid aria-label="PDF 頁面順序">{pages.map((page, index) => {
                const selected = selectedPageIds.has(page.id);
                return (
                  <PageCard
                    key={page.id} draggable={editingOrder && status !== "loading"} $editingOrder={editingOrder && status !== "loading"} $selected={selected} $dragOver={pageDragOverId === page.id}
                    onDragStart={(event) => { if (!editingOrder || status === "loading") { event.preventDefault(); return; } pageDragIdRef.current = page.id; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", page.id); }}
                    onDragOver={(event) => { if (!editingOrder || status === "loading" || !pageDragIdRef.current) return; event.preventDefault(); setPageDragOverId(page.id); }}
                    onDragLeave={() => setPageDragOverId((current) => current === page.id ? null : current)}
                    onDrop={(event) => { if (!editingOrder || status === "loading" || !pageDragIdRef.current) return; event.preventDefault(); const sourceId = pageDragIdRef.current; setPages((current) => movePdfItem(current, sourceId, page.id)); pageDragIdRef.current = null; setPageDragOverId(null); resetResult(); }}
                    onDragEnd={() => { pageDragIdRef.current = null; setPageDragOverId(null); }}
                  >
                    <PageSelect type="button" $selected={selected} aria-label={`${selected ? "取消選取" : "選取"} ${page.fileName} 第 ${page.sourcePageNumber} 頁`} onClick={() => togglePage(page.id)}>{selected ? <FaCheckSquare /> : <FaRegSquare />}</PageSelect>
                    <PagePosition>{index + 1}</PagePosition>
                    <ThumbnailStage
                      type="button"
                      disabled={!page.thumbnail}
                      aria-label={`放大預覽 ${page.fileName} 第 ${page.sourcePageNumber} 頁`}
                      title={page.thumbnailError || (page.thumbnail ? "點擊放大預覽" : undefined)}
                      onClick={() => openPagePreview(page)}
                    >{page.thumbnail ? <PageThumbnail page={page} /> : <ThumbnailLoading $error={page.thumbnailState === "error"}>{page.thumbnailState === "error" ? <FaFilePdf /> : <FaSyncAlt />}<span>{page.thumbnailState === "error" ? "無法顯示縮圖" : "產生縮圖中"}</span></ThumbnailLoading>}</ThumbnailStage>
                    <PageMeta><strong title={page.fileName}>{page.fileName}</strong><span>原第 {page.sourcePageNumber} 頁{page.annotations?.length ? ` ・ ${page.annotations.length} 個編輯項目` : ""}{page.crop ? " ・ 已裁切" : ""}{page.rotation ? ` ・ 已旋轉 ${page.rotation}°` : ""}</span></PageMeta>
                  </PageCard>
                );
              })}</PageGrid>
            </> : <EmptyState onChoose={() => inputRef.current?.click()} />}
          </Panel>
        )}
      </EditorMain>
    </EditorShell>
    {previewPage && <PdfPageEditor key={previewPage.id}
      page={{ ...previewPage, crop: pages.find((page) => page.id === previewPage.id)?.crop, annotations: pages.find((page) => page.id === previewPage.id)?.annotations || [] }}
      sourceFile={items.find((item) => item.id === previewPage.fileId)?.file}
      pageIndex={previewPageIndex} pageCount={pages.length}
      onClose={closePagePreview} onNavigate={showAdjacentPreview} onRotate={rotatePreviewPage}
      onCrop={(crop) => {
        setPages((current) => current.map((page) => page.id === previewPage.id ? { ...page, crop } : page));
        resetResult();
      }}
      onChange={(annotations) => {
        setPages((current) => current.map((page) => page.id === previewPage.id ? { ...page, annotations } : page));
        resetResult();
      }}
    />}
    </>
  );
}

function PageThumbnail({ page }) {
  const label = `${page.fileName} 第 ${page.sourcePageNumber} 頁預覽`;
  if (!page.annotations?.length && !page.crop) return <img src={page.thumbnail} alt={label} style={{ transform: `rotate(${page.rotation}deg)` }} />;
  const width = page.width || 595; const height = page.height || 842;
  const crop = page.crop || { x: 0, y: 0, width: 1, height: 1 };
  return <svg role="img" aria-label={label} viewBox={`${crop.x * width} ${crop.y * height} ${crop.width * width} ${crop.height * height}`} style={{ width: "90%", height: "90%", transform: `rotate(${page.rotation}deg)` }}>
    <image href={page.thumbnail} width={width} height={height} />
    {(page.annotations || []).filter((item) => item.originalRect).map((item) => <rect key={`mask-${item.id}`} x={item.originalRect.x * width} y={item.originalRect.y * height} width={item.originalRect.width * width} height={item.originalRect.height * height} fill="white" />)}
    {(page.annotations || []).map((item) => <image key={item.id} href={item.image} x={item.x * width} y={item.y * height} width={item.width * width} height={item.height * height} preserveAspectRatio="none" />)}
  </svg>;
}

function EmptyState({ onChoose }) {
  return <EmptyStateBox><FaFilePdf /><strong>尚未加入 PDF 或圖片</strong><span>一次可選擇多個檔案，再逐頁預覽、排序與編輯。</span><button type="button" onClick={onChoose}>選擇 PDF 或圖片</button></EmptyStateBox>;
}

const EditorShell = styled.div`
  display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 18px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const EditorSidebar = styled.section`
  align-self: start; display: grid; gap: 16px; padding: 18px;
  border: 1px solid var(--app-border, #d8d8d8); border-radius: 10px; background: var(--app-surface, #fbfbfb);
`;
const SidebarHeading = styled.div`
  display: grid; gap: 3px;
  span { font-size: 17px; font-weight: 800; }
  small { color: var(--app-muted-text, #6b6b6b); font-size: 12px; line-height: 1.45; }
`;
const FileDropZone = styled.div`
  display: grid; justify-items: center; gap: 7px; padding: 22px 12px;
  border: 1.5px dashed ${({ $active }) => ($active ? "#3867d6" : "var(--app-border, #c8c8c8)")}; border-radius: 10px;
  background: ${({ $active }) => ($active ? "rgba(56, 103, 214, .08)" : "var(--app-surface-soft, #f5f5f5)")}; text-align: center; cursor: pointer; transition: 150ms ease;
  &:hover { border-color: #3867d6; } strong { font-size: 14px; }
  span { color: var(--app-muted-text, #707070); font-size: 12px; line-height: 1.5; } input { display: none; }
`;
const DropIcon = styled.div`
  display: grid; place-items: center; width: 36px; height: 36px; border-radius: 50%;
  background: var(--app-nav-active-bg, #242424); color: var(--app-nav-active-text, #fff);
`;
const Field = styled.div`
  display: grid; gap: 7px; label { font-size: 13px; font-weight: 750; }
  input { min-width: 0; padding: 10px 11px; border: 1px solid var(--app-border, #cfcfcf); border-radius: 7px; background: var(--app-input-bg, #fff); color: var(--app-text, #1f1f1f); font: inherit; }
`;
const SummaryGrid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px;`;
const SummaryItem = styled.div`
  display: grid; gap: 2px; padding: 9px 4px; border-radius: 7px; background: var(--app-surface-soft, #f2f2f2); text-align: center;
  strong { font-size: 14px; } span { color: var(--app-muted-text, #707070); font-size: 10px; }
`;
const MergeButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 11px 14px; border: 0; border-radius: 8px;
  background: var(--app-nav-active-bg, #222); color: var(--app-nav-active-text, #fff); font-weight: 800; cursor: pointer;
  &:disabled { opacity: .42; cursor: not-allowed; } svg { animation: ${({ $loading }) => ($loading ? "pdf-merge-spin .9s linear infinite" : "none")}; }
  @keyframes pdf-merge-spin { to { transform: rotate(360deg); } }
`;
const StatusMessage = styled.div`
  padding: 10px 11px; border-radius: 7px; font-size: 12px; line-height: 1.5;
  background: ${({ $status }) => $status === "error" ? "#fff0ee" : $status === "success" ? "#eef8f0" : "var(--app-surface-soft, #f3f3f3)"};
  color: ${({ $status }) => $status === "error" ? "#8c1d18" : $status === "success" ? "#246a36" : "var(--app-subtle-text, #4f4f4f)"};
`;
const EditorMain = styled.div`min-width: 0;`;
const ViewTabs = styled.div`
  display: inline-flex; gap: 4px; margin-bottom: 10px; padding: 4px; border: 1px solid var(--app-border, #d4d4d4); border-radius: 8px; background: var(--app-surface-soft, #f3f3f3);
`;
const ViewTab = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 11px; border: 0; border-radius: 6px;
  background: ${({ $active }) => $active ? "var(--app-surface, #fff)" : "transparent"}; color: var(--app-text, #292929);
  box-shadow: ${({ $active }) => $active ? "0 1px 3px rgba(0,0,0,.12)" : "none"}; font-size: 12px; font-weight: 750; cursor: pointer;
`;
const Panel = styled.section`
  min-width: 0; padding: 16px; border: 1px solid var(--app-border, #d8d8d8); border-radius: 10px; background: var(--app-surface, #fbfbfb);
`;
const PanelHeader = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px;
  h3 { margin: 0 0 4px; font-size: 16px; } p { margin: 0; color: var(--app-muted-text, #707070); font-size: 12px; line-height: 1.45; }
`;
const HeaderActions = styled.div`display: inline-flex; gap: 6px;`;
const ClearButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px; flex: none; padding: 7px 9px; border: 1px solid var(--app-border, #d2d2d2); border-radius: 7px;
  background: transparent; color: var(--app-subtle-text, #555); font-size: 12px; cursor: pointer;
`;
const FileList = styled.div`display: grid; gap: 8px;`;
const FileRow = styled.div`
  display: grid; grid-template-columns: 22px 28px 38px minmax(0, 1fr) auto; align-items: center; gap: 10px; min-height: 66px; padding: 9px 10px;
  border: 1px solid ${({ $dragOver }) => ($dragOver ? "#3867d6" : "var(--app-border, #d7d7d7)")}; border-radius: 8px;
  background: ${({ $dragOver }) => ($dragOver ? "rgba(56, 103, 214, .07)" : "var(--app-surface, #fff)")};
  @media (max-width: 560px) { grid-template-columns: 20px 24px 32px minmax(0, 1fr); }
`;
const DragHandle = styled.div`display: grid; place-items: center; color: var(--app-muted-text, #888); cursor: grab;`;
const OrderNumber = styled.div`color: var(--app-muted-text, #747474); font-size: 12px; font-weight: 800; text-align: center;`;
const PdfIcon = styled.div`
  display: grid; place-items: center; width: 36px; height: 36px; border-radius: 7px; background: #fff0ee; color: #b32922; font-size: 18px;
`;
const FileDetails = styled.div`
  min-width: 0; display: grid; gap: 4px;
  strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--app-muted-text, #6f6f6f); font-size: 11px; }
`;
const RowActions = styled.div`display: inline-flex; gap: 4px; @media (max-width: 560px) { grid-column: 2 / -1; justify-content: flex-end; }`;
const SmallButton = styled.button`
  display: grid; place-items: center; width: 30px; height: 30px; border: 1px solid var(--app-border, #d5d5d5); border-radius: 6px;
  background: var(--app-surface, #fff); color: var(--app-subtle-text, #555); cursor: pointer; &:disabled { opacity: .3; cursor: not-allowed; }
`;
const PageToolbar = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; padding: 9px; border-radius: 8px; background: var(--app-surface-soft, #f3f3f3);
`;
const ToolbarButton = styled.button`
  display: inline-flex; align-items: center; gap: 5px; min-height: 32px; padding: 6px 8px;
  border: 1px solid ${({ $active }) => $active ? "#3867d6" : "var(--app-border, #d0d0d0)"}; border-radius: 6px;
  background: ${({ $active }) => $active ? "rgba(56,103,214,.1)" : "var(--app-surface, #fff)"}; color: var(--app-text, #333); font-size: 11px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: .38; cursor: not-allowed; }
`;
const SelectionCount = styled.span`color: var(--app-muted-text, #6f6f6f); font-size: 11px; padding: 0 4px;`;
const ToolbarDivider = styled.span`width: 1px; height: 23px; background: var(--app-border, #d0d0d0); @media (max-width: 600px) { display: none; }`;
const PageGrid = styled.div`display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px;`;
const pageOrderWiggle = keyframes`
  from { transform: rotate(-0.35deg); }
  to { transform: rotate(0.35deg); }
`;
const PageCard = styled.article`
  position: relative; min-width: 0; display: grid; grid-template-columns: minmax(170px, 240px) minmax(0, 1fr); align-items: center; gap: 16px;
  padding: 12px; border: 2px solid ${({ $selected, $dragOver }) => $dragOver ? "#3867d6" : $selected ? "#4775dc" : "transparent"};
  border-radius: 9px; background: ${({ $selected }) => $selected ? "rgba(56,103,214,.07)" : "var(--app-surface-soft, #f1f1f1)"}; transition: 120ms ease; cursor: ${({ $editingOrder }) => $editingOrder ? "grab" : "default"};
  &:hover, &:focus-within { border-color: #3867d6; box-shadow: 0 0 0 2px rgba(56,103,214,.14); }
  animation-name: ${({ $editingOrder }) => $editingOrder ? pageOrderWiggle : "none"};
  animation-duration: 260ms; animation-timing-function: ease-in-out; animation-iteration-count: infinite; animation-direction: alternate;
  transform-origin: center; &:nth-child(even) { animation-delay: -130ms; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); gap: 10px; }
`;
const PageSelect = styled.button`
  position: absolute; z-index: 2; top: 14px; left: 14px; display: grid; place-items: center; width: 28px; height: 28px; padding: 0;
  border: 1px solid ${({ $selected }) => $selected ? "#3867d6" : "rgba(0,0,0,.2)"}; border-radius: 6px;
  background: ${({ $selected }) => $selected ? "#3867d6" : "rgba(255,255,255,.92)"}; color: ${({ $selected }) => $selected ? "#fff" : "#555"}; cursor: pointer;
`;
const PagePosition = styled.div`
  position: absolute; z-index: 2; top: 14px; right: 14px; min-width: 25px; padding: 4px 6px; border-radius: 999px;
  background: rgba(25,25,25,.78); color: #fff; font-size: 10px; font-weight: 800; text-align: center;
`;
const ThumbnailStage = styled.button`
  display: grid; place-items: center; width: 100%; height: 270px; padding: 0; overflow: hidden; border: 1px solid #d7d7d7; border-radius: 5px; background: #e9e9e9; cursor: zoom-in;
  img { display: block; max-width: 90%; max-height: 90%; object-fit: contain; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,.14); transition: transform 180ms ease; }
  &:hover:not(:disabled), &:focus-visible { border-color: #3867d6; box-shadow: 0 0 0 2px rgba(56,103,214,.16); }
  &:disabled { cursor: default; }
  @media (max-width: 600px) { grid-column: 1 / -1; height: 320px; }
`;
const ThumbnailLoading = styled.div`
  display: grid; justify-items: center; gap: 8px; color: ${({ $error }) => $error ? "#9b3934" : "#777"}; font-size: 11px;
  svg { font-size: 22px; animation: ${({ $error }) => $error ? "none" : "pdf-thumb-spin .9s linear infinite"}; }
  @keyframes pdf-thumb-spin { to { transform: rotate(360deg); } }
`;
const PageMeta = styled.div`
  display: grid; gap: 3px; margin-top: 8px;
  strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; } span { color: var(--app-muted-text, #6e6e6e); font-size: 12px; }
`;

const TextOutput = styled.textarea`
  width: 100%; min-height: 470px; resize: vertical; padding: 14px; border: 1px solid var(--app-border, #d1d1d1); border-radius: 8px;
  background: var(--app-input-bg, #fff); color: var(--app-text, #222); font: 13px/1.7 ui-monospace, SFMono-Regular, Menlo, monospace;
`;
const TextEmpty = styled.div`
  display: grid; justify-items: center; align-content: center; gap: 8px; min-height: 400px; color: var(--app-muted-text, #707070); text-align: center;
  > svg { font-size: 34px; opacity: .65; } strong { color: var(--app-text, #292929); } span { font-size: 12px; }
  button { margin-top: 6px; padding: 8px 12px; border: 1px solid var(--app-border, #ccc); border-radius: 7px; background: var(--app-surface, #fff); cursor: pointer; }
`;
const EmptyStateBox = styled.div`
  display: grid; justify-items: center; align-content: center; gap: 8px; min-height: 330px; border: 1px dashed var(--app-border, #d2d2d2); border-radius: 9px;
  color: var(--app-muted-text, #707070); text-align: center;
  > svg { margin-bottom: 4px; color: #bd3b34; font-size: 38px; opacity: .72; } strong { color: var(--app-text, #2a2a2a); font-size: 15px; }
  span { max-width: 310px; font-size: 12px; line-height: 1.6; }
  button { margin-top: 5px; padding: 8px 13px; border: 1px solid var(--app-border, #ccc); border-radius: 7px; background: var(--app-surface, #fff); color: var(--app-text, #2a2a2a); font-weight: 750; cursor: pointer; }
`;
